import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Button, 
  Card, 
  CardContent, 
  Divider,
  Chip,
  IconButton,
  Tabs,
  Tab
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { API_ENDPOINTS } from "../config/api.config";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import FolderIcon from '@mui/icons-material/Folder';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  
  // States per team memberships
  const [userTeams, setUserTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState(null);

  // Fetch user details
  const fetchUser = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[USER_DETAIL] Fetching user details for ID:', id);
      
      // Usa l'endpoint che funziona per tutti gli utenti e filtra per ID
      const response = await fetch(API_ENDPOINTS.USERS);
      if (!response.ok) throw new Error("Error fetching users");
      const data = await response.json();
      const users = data.users || data || [];
      
      // Trova l'utente specifico per ID
      const foundUser = users.find(u => String(u.id) === String(id));
      
      if (!foundUser) {
        throw new Error("User not found");
      }
      
      console.log('[USER_DETAIL] User found:', foundUser);
      setUser(foundUser);
    } catch (err) {
      console.error('[USER_DETAIL] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user teams and project assignments.
  const fetchUserTeams = async () => {
    if (!id) return;
    
    setTeamsLoading(true);
    setTeamsError(null);
    try {
      console.log('[USER_TEAMS] Fetching user teams and project assignments');
      
      const [teamsResponse, projectsResponse] = await Promise.all([
        fetch(`${API_ENDPOINTS.TEAMS}?user_id=${id}&get_user_memberships=true`),
        fetch(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${id}`),
      ]);
      
      if (!teamsResponse.ok) {
        throw new Error("Error fetching user teams");
      }
      if (!projectsResponse.ok) {
        throw new Error("Error fetching user projects");
      }
      
      const teamsData = await teamsResponse.json();
      const projectsData = await projectsResponse.json();
      const teams = teamsData.teams || [];
      const userProjects = projectsData.user_projects_with_references || [];
      const teamsByProjectId = new Map(
        teams.map((team) => [String(team.project_id), team])
      );

      const projectRows = userProjects.map((assignment) => {
        const project = assignment.project || {};
        const role = assignment.project_role || {};
        const customer = assignment.customer || {};
        const team = teamsByProjectId.get(String(project.id));

        return {
          id: `${project.id}-${team?.id || 'no-team'}`,
          project_id: project.id,
          project_name: project.name || '',
          customer_name: customer.name || '',
          project_role_name: role.name || '',
          project_role_code: role.code || '',
          name: team?.name || 'No team assigned',
          description: team?.description || '',
          has_team: !!team,
          created_at: team?.membership_created_at || assignment.assigned_at,
        };
      });
      const projectIdsFromAssignments = new Set(
        projectRows.map((row) => String(row.project_id))
      );
      const teamOnlyRows = teams
        .filter((team) => !projectIdsFromAssignments.has(String(team.project_id)))
        .map((team) => ({
          id: `${team.project_id}-${team.id}`,
          project_id: team.project_id,
          project_name: team.project_name || '',
          customer_name: '',
          project_role_name: '',
          project_role_code: '',
          name: team.name || '',
          description: team.description || '',
          has_team: true,
          created_at: team.membership_created_at || team.created_at,
        }));
      const rows = [...projectRows, ...teamOnlyRows].sort((a, b) =>
        String(a.project_name || '').localeCompare(String(b.project_name || ''))
      );
      
      console.log('[USER_TEAMS] Project/team rows found:', rows.length);
      setUserTeams(rows);
    } catch (err) {
      console.error('[USER_TEAMS] Fetch error:', err);
      setTeamsError(`Error loading user teams and projects: ${err.message}`);
    } finally {
      setTeamsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchUser();
      fetchUserTeams();
    }
  }, [id]);

  const handleBack = () => {
    navigate('/users');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleEditUser = () => {
    navigate('/users', { state: { editUserId: id } });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // TabPanel component helper
  function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`user-tabpanel-${index}`}
        aria-labelledby={`user-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 4 }}>
            {children}
          </Box>
        )}
      </Box>
    );
  }

  // Colonne per la tabella dei team
  const teamsColumns = [
    { field: 'project_name', headerName: 'Project', width: 200, flex: 1 },
    { field: 'customer_name', headerName: 'Customer', width: 160, flex: 1 },
    { field: 'project_role_name', headerName: 'Project Role', width: 160, flex: 1 },
    {
      field: 'name',
      headerName: 'Team Name',
      width: 170,
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontStyle: params.row.has_team ? 'normal' : 'italic',
            color: params.row.has_team ? 'text.primary' : 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { field: 'description', headerName: 'Team Description', width: 250, flex: 1 },
    { 
      field: 'created_at', 
      headerName: 'Assigned / Joined',
      width: 160,
      valueFormatter: (value) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleDateString('en-GB');
      }
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {error}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
              Back to Users
            </Button>
            <Button 
              variant="text" 
              size="small" 
              onClick={fetchUser}
              sx={{ ml: 1 }}
            >
              🔄 Retry
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box my={4}>
        <Alert severity="warning">
          <Typography variant="body1">
            User not found.
          </Typography>
          <Button onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ mt: 1 }}>
            Back to Users
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box my={4}>
        {/* Header con titolo, back button e edit button */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontSize: '1.5rem', flexGrow: 1 }}>
            User Details for
            <Box
              component="span"
              sx={{
                display: { xs: 'block', md: 'inline' },
                bgcolor: '#e0e0e0',
                color: '#607c89',
                fontWeight: 500,
                fontSize: '0.9em',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                ml: { xs: 0, md: 1 },
                mt: { xs: 1, md: 0 }
              }}
            >
              {user.username}
            </Box>
          </Typography>
          <IconButton onClick={handleEditUser} sx={{ ml: 2 }}>
            <EditIcon />
          </IconButton>
        </Box>

        {/* Tabs per organizzare le informazioni */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="user information tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#388e3c',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#388e3c',
                },
              }}
            >
              <Tab
                icon={<InfoIcon />}
                label="General"
                id="user-tab-0"
                aria-controls="user-tabpanel-0"
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper + span': {
                    display: { xs: 'none', sm: 'inline' },
                    marginLeft: { sm: '8px' }
                  }
                }}
              />
              <Tab
                icon={<GroupsIcon />}
                label="Teams & Projects"
                id="user-tab-1"
                aria-controls="user-tabpanel-1"
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper + span': {
                    display: { xs: 'none', sm: 'inline' },
                    marginLeft: { sm: '8px' }
                  }
                }}
              />
              <Tab
                icon={<SettingsIcon />}
                label="System"
                id="user-tab-2"
                aria-controls="user-tabpanel-2"
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper + span': {
                    display: { xs: 'none', sm: 'inline' },
                    marginLeft: { sm: '8px' }
                  }
                }}
              />
            </Tabs>
          </Box>

          {/* Tab Panel 1: General Information */}
          <TabPanel value={currentTab} index={0}>
            <Box sx={{ maxWidth: '600px' }}>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Username
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.username || 'Not set'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.email || 'Not set'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Role
                </Typography>
                <Chip 
                  label={user.role?.name || user.role || 'Unknown'} 
                  size="small" 
                  variant="outlined"
                  color={user.role?.name === 'Admin' ? 'error' : 'default'}
                  sx={{ textTransform: 'capitalize' }}
                />
                {user.role?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {user.role.description}
                  </Typography>
                )}
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  People Manager
                </Typography>
                {user.people_manager ? (
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {user.people_manager.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.people_manager.email}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    No people manager assigned
                  </Typography>
                )}
              </Box>
            </Box>
          </TabPanel>

          {/* Tab Panel 2: Teams & Projects */}
          <TabPanel value={currentTab} index={1}>
            <Box display="flex" alignItems="center" mb={3}>
              <GroupsIcon sx={{ mr: 2, color: '#607c89' }} />
              <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                Teams & Projects ({userTeams.length})
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 3 }} />

            {teamsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading teams and projects...
                </Typography>
              </Box>
            ) : teamsError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {teamsError}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button 
                    variant="text" 
                    size="small" 
                    onClick={fetchUserTeams}
                    sx={{ mr: 1 }}
                  >
                    🔄 Retry
                  </Button>
                </Box>
              </Alert>
            ) : userTeams.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body1">
                  This user is not associated with any projects yet.
                </Typography>
              </Alert>
            ) : (
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={userTeams}
                  columns={teamsColumns}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10, page: 0 },
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  pagination
                  sx={{ bgcolor: 'white' }}
                  disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                />
              </Box>
            )}
          </TabPanel>

          {/* Tab Panel 3: System Information */}
          <TabPanel value={currentTab} index={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  User ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '1.1rem' }}>
                  #{user.id}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Role ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                  {user.role_id || 'N/A'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Level ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                  {user.level_id || 'N/A'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Company ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                  {user.company_id || 'N/A'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Created At
                </Typography>
                <Typography variant="body1">
                  {formatDate(user.created_at)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Last Updated
                </Typography>
                <Typography variant="body1">
                  {formatDate(user.updated_at)}
                </Typography>
              </Box>
            </Box>
          </TabPanel>
        </Card>
      </Box>
    </Box>
  );
} 